import React, { useState, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, RefreshCw, X, AlertTriangle } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { TourTooltip } from '../../common/TourTooltip';

interface CameraCaptureProps {
    onBack: () => void;
    onCaptureComplete: (images: string[]) => void;
    subjectName: string;
    subjectCode: string;
    initialImages?: string[];
}

export const CameraCapture: React.FC<CameraCaptureProps> = ({ onBack, onCaptureComplete, initialImages = [] }) => {
    const webcamRef = useRef<Webcam>(null);
    const [images, setImages] = useState<string[]>(initialImages);
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
    const [flash, setFlash] = useState(false);
    const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const zoomLevel = 1; // Zoom disabled, kept as constant for crop calculations

    // Tour state
    const [runTour, setRunTour] = useState(false);
    const tourSteps: Step[] = [
        {
            target: '.tour-camera-view',
            title: 'Camera View',
            content: 'Position your answer sheet within the frame. Ensure good lighting for best results.',
            disableBeacon: true,
        },
        {
            target: '.tour-capture-btn',
            title: 'Capture Button',
            content: 'Tap here to take a photo of the page. You can capture multiple pages.',
        },
        {
            target: '.tour-camera-gallery',
            title: 'Captured Pages',
            content: 'Your captured pages will appear here. Tap to preview or delete them.',
        },
        {
            target: '.tour-submit-btn',
            title: 'Submit',
            content: 'Once you have captured all pages, click here to proceed to review.',
        }
    ];

    React.useEffect(() => {
        const hasSeenTour = localStorage.getItem('hasSeenCameraTour');
        if (!hasSeenTour) {
            // Small delay to ensure camera is loaded
            setTimeout(() => setRunTour(true), 1000);
        }
    }, []);

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status } = data;
        const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
        if (finishedStatuses.includes(status)) {
            setRunTour(false);
            localStorage.setItem('hasSeenCameraTour', 'true');
        }
    };

    // Camera constraints - use ideal for better mobile support
    const videoConstraints = {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        facingMode: facingMode
    };

    // Image Quality Analysis (Laplacian Variance Algorithm)
    const analyzeImageQuality = (imageSrc: string): Promise<string[]> => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                // Use higher resolution for better detection
                const width = 1000;
                const scaleFactor = width / img.width;
                const height = Math.round(img.height * scaleFactor);

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                if (!ctx) return resolve([]);

                ctx.drawImage(img, 0, 0, width, height);

                // Analyze center 60% of image (where document content is)
                const marginX = Math.floor(width * 0.2);
                const marginY = Math.floor(height * 0.2);
                const analyzeWidth = width - (marginX * 2);
                const analyzeHeight = height - (marginY * 2);

                const imageData = ctx.getImageData(marginX, marginY, analyzeWidth, analyzeHeight);
                const data = imageData.data;

                // 1. Convert to grayscale and calculate brightness
                const grayData = new Uint8ClampedArray(analyzeWidth * analyzeHeight);
                let totalBrightness = 0;

                for (let i = 0; i < data.length; i += 4) {
                    const brightness = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
                    grayData[i / 4] = brightness;
                    totalBrightness += brightness;
                }

                const avgBrightness = totalBrightness / (data.length / 4);

                // 2. Calculate Laplacian variance for blur detection
                let meanLaplacian = 0;
                const laplacianValues: number[] = [];

                for (let y = 1; y < analyzeHeight - 1; y++) {
                    for (let x = 1; x < analyzeWidth - 1; x++) {
                        const idx = y * analyzeWidth + x;
                        // Full 8-neighbor Laplacian kernel
                        const laplacian =
                            grayData[idx - analyzeWidth - 1] + grayData[idx - analyzeWidth] + grayData[idx - analyzeWidth + 1] +
                            grayData[idx - 1] + grayData[idx + 1] +
                            grayData[idx + analyzeWidth - 1] + grayData[idx + analyzeWidth] + grayData[idx + analyzeWidth + 1]
                            - (8 * grayData[idx]);

                        const absLap = Math.abs(laplacian);
                        laplacianValues.push(absLap);
                        meanLaplacian += absLap;
                    }
                }

                meanLaplacian /= laplacianValues.length;

                let variance = 0;
                for (let i = 0; i < laplacianValues.length; i++) {
                    variance += Math.pow(laplacianValues[i] - meanLaplacian, 2);
                }
                variance /= laplacianValues.length;

                const issues: string[] = [];

                // Blur Threshold (Extremely lowered to 50 for max mobile compatibility)
                if (variance < 50) {
                    issues.push("Image appears blurry");
                }

                // Brightness Thresholds (Lenient)
                if (avgBrightness < 40) {
                    issues.push("High darkness detected");
                } else if (avgBrightness < 70) {
                    issues.push("Low light detected");
                }

                console.log(`Image Analysis - Blur Score (Variance): ${variance.toFixed(2)}, Brightness: ${avgBrightness.toFixed(2)}`);

                resolve(issues);
            };
            img.src = imageSrc;
        });
    };

    const capture = useCallback(async () => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (imageSrc) {
            setFlash(true);
            setTimeout(() => setFlash(false), 150);

            // Crop the image to match the visible capture box overlay
            // This ensures only the content inside the white border is saved
            const croppedImage = await new Promise<string>((resolve) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    if (!ctx) return resolve(imageSrc);

                    // Match the capture box dimensions (99% width, 98% height)
                    // These percentages match the CSS classes: w-[99%] h-[98%]
                    const cropWidthPercent = 0.99;
                    const cropHeightPercent = 0.98;

                    {/* Adjust crop size based on zoom level */ }
                    {/* When zoomed in, the visible area is smaller on the original image */ }
                    const usedZoomLevel = 1; // Forced to 1x for now as zoom is disabled
                    const cropWidth = (img.width * cropWidthPercent) / usedZoomLevel;
                    const cropHeight = (img.height * cropHeightPercent) / usedZoomLevel;

                    // Center the crop area (same as CSS centering)
                    const cropX = (img.width - cropWidth) / 2;
                    const cropY = (img.height - cropHeight) / 2;

                    canvas.width = cropWidth;
                    canvas.height = cropHeight;

                    // Extract only the area inside the capture box
                    ctx.drawImage(
                        img,
                        cropX, cropY, cropWidth, cropHeight,  // Source: crop from center
                        0, 0, cropWidth, cropHeight           // Destination: full canvas
                    );

                    resolve(canvas.toDataURL('image/jpeg', 0.95));
                };
                img.src = imageSrc;
            });

            // Analyze cropped image
            const issues = await analyzeImageQuality(croppedImage);

            // Verify quality and BLOCK if issues found
            if (issues.length > 0) {
                // Warning Toast - Do NOT save image
                toast.custom((t) => (
                    <div
                        className="text-white px-6 py-4 rounded-xl flex items-start gap-4 shadow-2xl max-w-sm border-2 border-white/20 select-none"
                        style={{ backgroundColor: '#dc2626', opacity: 1 }}
                    >
                        <AlertTriangle className="w-6 h-6 text-white shrink-0" />
                        <div className="flex-1">
                            <h4 className="font-bold text-base text-white">Capture Rejected</h4>
                            <p className="text-sm mt-1 text-white/95 font-medium leading-tight">
                                {issues.join(", ")}.<br />
                                Please retake.
                            </p>
                        </div>
                        <button
                            onClick={() => toast.dismiss(t)}
                            className="text-white hover:text-white/80 p-1 hover:bg-white/10 rounded-full transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                ), { duration: 4000, position: 'top-center' });
            } else {
                // No issues - Save cropped image
                setImages((prev) => [...prev, croppedImage]);
            }
        }
    }, [webcamRef, images.length, zoomLevel]);

    const handleDeleteImage = (index: number) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
        if (selectedImageIndex === index) setSelectedImageIndex(null);
    };

    const handleConfirmDeleteAll = () => {
        setImages([]);
        setShowDeleteConfirm(false);
        toast.success('All pages deleted', { position: 'top-center' });
    };

    const toggleCamera = () => {
        setFacingMode(prev => prev === "user" ? "environment" : "user");
    };

    return (
        <div className="fixed inset-0 bg-black z-50 flex flex-col text-white overflow-hidden font-sans">
            <Toaster />

            {/* Flash Effect */}
            <AnimatePresence>
                {flash && (
                    <motion.div
                        initial={{ opacity: 0.8 }}
                        animate={{ opacity: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 bg-white z-[60] pointer-events-none"
                    />
                )}
            </AnimatePresence>

            {/* Top Bar Overlay */}
            <div className="absolute top-0 left-0 right-0 p-4 z-40 flex justify-between items-center bg-gradient-to-b from-black/80 to-transparent pt-6">
                <button
                    onClick={onBack}
                    className="p-2.5 bg-black/40 backdrop-blur-md rounded-full hover:bg-white/10 transition-colors"
                >
                    <X className="w-6 h-6" />
                </button>


            </div>

            {/* Main Camera Viewport */}
            <div className="tour-camera-view flex-1 relative overflow-hidden bg-black">
                <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    width={1920}
                    height={1080}
                    videoConstraints={videoConstraints}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-200 ease-out"
                // style={{ transform: `scale(${zoomLevel})` }} // Zoom disabled
                />

                {/* Guide Overlay */}
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="w-[99%] h-[98%] border-[3px] border-white/80 rounded-3xl relative shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]">
                        {/* Corner Accents */}
                        <div className="absolute top-0 left-0 w-8 h-8 border-t-[6px] border-l-[6px] border-white -mt-[3px] -ml-[3px] rounded-tl-lg" />
                        <div className="absolute top-0 right-0 w-8 h-8 border-t-[6px] border-r-[6px] border-white -mt-[3px] -mr-[3px] rounded-tr-lg" />
                        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-[6px] border-l-[6px] border-white -mb-[3px] -ml-[3px] rounded-bl-lg" />
                        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-[6px] border-r-[6px] border-white -mb-[3px] -mr-[3px] rounded-br-lg" />

                        {/* Zoom Indicator (Optional visual feedback) - Disabled */}
                        {/* {zoomLevel > 1 && (
                            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 rounded-full">
                                {zoomLevel.toFixed(1)}x
                            </div>
                        )} */}
                    </div>
                </div>
                <button
                    onClick={() => onCaptureComplete(images)}
                    disabled={images.length === 0}
                    className={`tour-submit-btn absolute bottom-6 right-6 z-50 px-4 py-2 rounded-full font-bold text-xs backdrop-blur-md transition-all flex items-center gap-2 ${images.length > 0
                        ? 'bg-white text-black hover:bg-zinc-200 shadow-lg'
                        : 'bg-zinc-800/50 text-zinc-500 cursor-not-allowed border border-white/10'
                        }`}
                >
                    Submit
                    {images.length > 0 && (
                        <span className="bg-black text-white text-[10px] px-1.5 py-0.5 rounded-full min-w-[1rem] text-center">
                            {images.length}
                        </span>
                    )}
                </button>
            </div>

            {/* Bottom Controls Area - Simplified & Compact */}
            <div className="relative z-40 bg-black/40 backdrop-blur-sm pt-3 pb-6 px-4 flex flex-col items-center gap-3 rounded-t-3xl border-t border-white/10 -mt-4">

                {/* Thumbnails Row */}
                <div className="tour-camera-gallery w-full h-16 mb-1">
                    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide mask-linear-fade justify-center h-full items-center">
                        <AnimatePresence mode='popLayout'>
                            {images.length === 0 ? (
                                <div className="text-white/40 text-[10px] font-medium">Captured pages will appear here</div>
                            ) : (
                                images.map((img, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 10, scale: 0.8 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.5 }}
                                        className="relative shrink-0"
                                    >
                                        <div
                                            className="h-12 w-9 rounded-md bg-zinc-800 border-[1px] border-white/30 overflow-hidden cursor-pointer shadow-sm"
                                            onClick={() => setSelectedImageIndex(idx)}
                                        >
                                            <img src={img} alt={`Page ${idx + 1}`} className="w-full h-full object-cover" />
                                            <div className="absolute top-0 right-0 text-[6px] font-bold bg-black/60 px-1 rounded-bl text-white backdrop-blur-sm">
                                                {idx + 1}
                                            </div>
                                        </div>
                                        <div
                                            className="absolute -top-1.5 -right-1.5 z-10"
                                        >
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDeleteImage(idx); }}
                                                className="bg-zinc-800 border border-white/20 text-white rounded-full p-0.5 shadow-sm hover:bg-zinc-700 transition-colors"
                                            >
                                                <X className="w-2 h-2" />
                                            </button>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Main Action Row */}
                <div className="flex items-center justify-center gap-8 w-full max-w-xs px-2">
                    {/* Delete All Button */}
                    <button
                        onClick={() => images.length > 0 && setShowDeleteConfirm(true)}
                        className={`p-3 rounded-full transition-colors backdrop-blur-md border border-white/10 ${images.length > 0
                            ? 'bg-zinc-900/80 text-white hover:bg-zinc-800'
                            : 'bg-zinc-900/40 text-zinc-600 cursor-not-allowed'
                            }`}
                        title="Delete all pages"
                    >
                        <Trash2 className="w-5 h-5" />
                    </button>

                    {/* Shutter Button */}
                    <button
                        onClick={capture}
                        className="tour-capture-btn relative group cursor-pointer"
                    >
                        <div className="w-16 h-16 rounded-full border-[3px] border-white flex items-center justify-center bg-transparent group-active:scale-95 transition-transform duration-100 shadow-lg shadow-black/20">
                            <div className="w-14 h-14 rounded-full bg-white group-hover:bg-zinc-200 transition-colors" />
                        </div>
                    </button>

                    {/* Switch Camera */}
                    <button
                        onClick={toggleCamera}
                        className="p-3 rounded-full bg-zinc-900/80 text-white hover:bg-zinc-800 transition-colors backdrop-blur-md border border-white/10"
                    >
                        <RefreshCw className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteConfirm && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowDeleteConfirm(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="bg-zinc-900 border border-white/10 p-6 rounded-2xl w-full max-w-sm relative z-10 shadow-2xl"
                        >
                            <div className="flex flex-col items-center text-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center text-red-500">
                                    <Trash2 className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white mb-1">Delete all pages?</h3>
                                    <p className="text-zinc-400 text-sm">
                                        This action cannot be undone. All {images.length} captured pages will be permanently deleted.
                                    </p>
                                </div>
                                <div className="flex gap-3 w-full mt-2">
                                    <button
                                        onClick={() => setShowDeleteConfirm(false)}
                                        className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl font-medium transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleConfirmDeleteAll}
                                        className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors"
                                    >
                                        Delete All
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Full Screen Image Preview Modal */}
            {selectedImageIndex !== null && (
                <div className="absolute inset-0 z-[100] bg-black flex flex-col animate-in fade-in duration-200">
                    <div className="flex justify-between items-center p-4 pt-8">
                        <h3 className="text-white font-medium text-lg">Page {selectedImageIndex + 1}</h3>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => handleDeleteImage(selectedImageIndex)}
                                className="p-2.5 bg-red-500/10 text-red-500 rounded-full hover:bg-red-500/20 transition-colors"
                            >
                                <Trash2 className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setSelectedImageIndex(null)}
                                className="p-2.5 bg-zinc-800 rounded-full text-white hover:bg-zinc-700"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 flex items-center justify-center p-4">
                        <img
                            src={images[selectedImageIndex]}
                            alt="Preview"
                            className="max-h-full max-w-full object-contain rounded-lg"
                        />
                    </div>
                </div>
            )}

            <Joyride
                steps={tourSteps}
                run={runTour}
                continuous
                showProgress
                showSkipButton
                tooltipComponent={TourTooltip}
                callback={handleJoyrideCallback}
                floaterProps={{
                    disableAnimation: true,
                }}
                styles={{
                    spotlight: {
                        borderRadius: '24px', // Slightly larger radius for camera buttons
                    },
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    },
                    options: {
                        zIndex: 10000,
                    }
                }}
            />
        </div>
    );
};
